"use client";

import React, { useEffect, useState } from "react";
import { SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import parse from "html-react-parser";
import CountUp from 'react-countup';
import { SectionWrapper } from "../components/SectionWraper";

interface Status1Props { 
  darkMode?: boolean;
  themeChangeOnScroll?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  title?: string;
  statsItems?: ItemType[];   
  attributes?: React.HTMLProps<HTMLElement>;
}

type ItemType = {
  value?: string;  // Keep as string since it can contain special formats
  label?: string;
};

const parseNumericValue = (value: string | undefined | null): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return 0;
  

  // Extract number and suffix - updated regex to handle numbers without prefix/suffix
  const match = value.match(/^([^0-9]*)([\d,.]+)([^0-9]*)$/);
  if (!match) {
    // If no match, try to parse the whole string as a number
    const num = parseFloat(value.replace(/,/g, ''));
    return isNaN(num) ? 0 : num;
  }
  
  const [, , num] = match;
  // Remove commas and convert to number
  const parsedNum = parseFloat(num.replace(/,/g, ''));
  return isNaN(parsedNum) ? 0 : parsedNum;
};

const extractPrefixSuffix = (value: string | undefined | null): { prefix: string, suffix: string } => {
  if (!value || typeof value !== 'string') return { prefix: '', suffix: '' };
  
  // Updated regex to handle numbers without prefix/suffix
  const match = value.match(/^([^0-9]*)([\d,.]+)([^0-9]*)$/);
  if (!match) {
    // If no match, assume the whole string is a number
    return { prefix: '', suffix: '' };
  }
  
  const [, prefix, , suffix] = match;
  return { prefix: prefix || '', suffix: suffix || '' };
};

const Stats1 = ({
  darkMode = false,
  themeChangeOnScroll = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Stats 1",
  title,
  statsItems,
  attributes,
}: Status1Props) => {  
  // Prevent hydration mismatch by only rendering CountUp after client mount
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="status1"
      attributes={attributes}
    >  
      <div className="container" data-aos="fade-up">
        {title && (
          <h2 className="sm:max-w-[500px] lg:max-w-[800px] text-center sm:text-left">
            {parse(title)}
          </h2>
        )}
        <div className="mt-6 md:mt-16 grid gap-x-6 md:grid-cols-2 lg:grid-cols-3" data-aos="fade-up" data-aos-delay="100">
          {statsItems?.map((statsItem, index) => (
            <div key={index} className="flex flex-col gap-4 border-b py-6">
              <h3 className="text-brand" suppressHydrationWarning>
                {(() => {
                  if (!statsItem.value) return null;
                  const { prefix, suffix } = extractPrefixSuffix(statsItem.value);
                  const numericValue = parseNumericValue(statsItem.value);
                  
                  return (
                    <>
                      {prefix}
                      {isMounted ? (
                        <CountUp 
                          end={numericValue} 
                          duration={1.5}
                          separator="," 
                          enableScrollSpy
                          scrollSpyOnce
                          scrollSpyDelay={300}
                        />
                      ) : (
                        // Show final value on server-side to prevent layout shift
                        numericValue.toLocaleString()
                      )}
                      {suffix}
                    </>
                  );
                })()}
              </h3>
              {statsItem.label && (
                <div className="text-muted-foreground paragraph-base">{parse(statsItem.label)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Stats1 };



